import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { m, AnimatePresence } from 'framer-motion'
import { X, ZoomIn, ZoomOut } from 'lucide-react'
import { getImageUrl } from '../../../utils/imageUtils'

interface ImageModalProps {
    isOpen: boolean
    onClose: () => void
    imageSrc: string
    imageAlt: string
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, onClose, imageSrc, imageAlt }) => {
    const [zoom, setZoom] = React.useState(1)

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            console.log('Modal opened with image:', imageSrc)
        } else {
            document.body.style.overflow = 'unset'
            setZoom(1)
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, imageSrc])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                console.log('Escape pressed, closing modal')
                onClose()
            }
        }
        if (isOpen) {
            window.addEventListener('keydown', handleEscape)
        }
        return () => window.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    const handleZoomIn = (e: React.MouseEvent) => {
        e.stopPropagation()
        setZoom((prev) => Math.min(prev + 0.25, 3))
    }

    const handleZoomOut = (e: React.MouseEvent) => {
        e.stopPropagation()
        setZoom((prev) => Math.max(prev - 0.25, 0.5))
    }

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation()
        console.log('Close button clicked')
        onClose()
    }

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            console.log('Backdrop clicked, closing modal')
            onClose()
        }
    }

    if (!isOpen || !imageSrc) return null

    const modalContent = (
        <AnimatePresence>
            <m.div
                key="modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleBackdropClick}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 0,
                    padding: 0,
                }}
            >
                {/* Controls */}
                <div
                    style={{
                        position: 'fixed',
                        top: '1rem',
                        right: '1rem',
                        display: 'flex',
                        gap: '0.5rem',
                        zIndex: 1000000,
                    }}
                >
                    <m.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleZoomOut}
                        style={{
                            padding: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '0.5rem',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        aria-label="Zoom out"
                    >
                        <ZoomOut size={24} />
                    </m.button>
                    <m.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleZoomIn}
                        style={{
                            padding: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '0.5rem',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        aria-label="Zoom in"
                    >
                        <ZoomIn size={24} />
                    </m.button>
                    <m.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleClose}
                        style={{
                            padding: '0.75rem',
                            backgroundColor: '#dc2626',
                            borderRadius: '0.5rem',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        aria-label="Close"
                    >
                        <X size={24} />
                    </m.button>
                </div>

                {/* Zoom Level Indicator */}
                <div
                    style={{
                        position: 'fixed',
                        top: '1rem',
                        left: '1rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(12px)',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        color: 'white',
                        fontWeight: '500',
                        zIndex: 1000000,
                    }}
                >
                    {Math.round(zoom * 100)}%
                </div>

                {/* Image Container */}
                <m.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '85vh',
                        overflow: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <m.img
                        src={getImageUrl(imageSrc)}
                        alt={imageAlt}
                        animate={{ scale: zoom }}
                        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                        style={{
                            width: 'auto',
                            height: 'auto',
                            maxWidth: 'none',
                            borderRadius: '0.5rem',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            transformOrigin: 'center center',
                            minWidth: '300px',
                            minHeight: '300px',
                        }}
                        draggable={false}
                        onError={(e) => {
                            console.error('Image failed to load:', imageSrc)
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                        }}
                    />
                </m.div>

                {/* Caption */}
                {imageAlt && (
                    <div
                        style={{
                            position: 'fixed',
                            bottom: '1rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(12px)',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '0.5rem',
                            color: 'white',
                            maxWidth: '90vw',
                            textAlign: 'center',
                            zIndex: 1000000,
                        }}
                    >
                        {imageAlt}
                    </div>
                )}
            </m.div>
        </AnimatePresence>
    )

    return createPortal(modalContent, document.body)
}

export default ImageModal
